import {Modal, ModalBody, ModalContent, ModalHeader} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {GoogleMap, MarkerF, useJsApiLoader} from '@react-google-maps/api';
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import {Globe, MapPin, Phone, Mail, Navigation, Search, Building2} from 'lucide-react'
import {useState} from 'react'

import {config} from "../config/config";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import StarIcon from "../landing/icons/StarIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import services from "../services";
import {sportsForSurface, useSports} from "../sports/registry.ts";

export const Route = createFileRoute('/club-directory')({
  component: RouteComponent,
})

// The sport filter, from the sport registry's 'directory' surface. The filter sends the stored key
// (so 'pickelball' — the backend normalises either spelling and listings are stored under it).
// Read at call time, never at module load (see sports/registryReadAtRender.test.ts).
const sportOptions = () => [
  {value: '', label: 'All sports'},
  ...sportsForSurface('directory').map((entry) => ({value: entry.key, label: entry.label})),
]

const sportLabel = (k: string) => sportOptions().find((s) => s.value === k)?.label || k

function RouteComponent() {
  useSports() // re-render with the full sport list once the live registry lands
  const navigate = useNavigate()
  const [sport, setSport] = useState('')
  const [q, setQ] = useState('')
  const [active, setActive] = useState<any | null>(null)

  const listQuery = useQuery({
    queryKey: ['club-directory', sport, q],
    queryFn:  () => services.clubDirectory.list({sport: sport || undefined, q: q || undefined}),
  })

  const clubs: any[] = listQuery.data?.clubs || []

  return (
    <main className="min-h-screen bg-[#EDEEF4] font-inter text-[#071a5f]">
      <title>Sports Club Directory | Find & List Clubs Near You | ScorePal</title>
      <meta name="description" content="Browse sports clubs near you — padel, tennis, pickleball, squash, badminton, basketball and more. List your club for free in the ScorePal directory." />
      <link rel="canonical" href={`${config.publicUrl}/club-directory/`} />

      <section className="relative overflow-hidden p-4">
        <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
          <div className="relative z-20">
            <LandingNav />
          </div>

          <div className="container mx-auto px-6 pt-8 lg:px-12">
            <div className="max-w-3xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90">
                <StarIcon className="text-white" size={16} />
                Sports clubs across every sport
              </span>

              <h1 className="mb-5 font-bebas text-[clamp(40px,6vw,72px)] font-bold uppercase leading-none tracking-wide text-white">
                Find a sports club near you
              </h1>

              <p className="mb-8 max-w-2xl font-inter text-base leading-relaxed text-white/85 lg:text-xl">
                Discover sports clubs in your area — padel, tennis, pickleball, squash, badminton,
                basketball and more. Run a club? List it for free so players can find you.
              </p>

              <button
                type="button"
                onClick={() => navigate({to: "/list-club/" as any})}
                className="inline-flex items-center gap-3 rounded-full bg-[#EDEEF4] py-1 pl-6 pr-1 font-semibold text-[#082F8A] shadow-lg transition hover:bg-white"
              >
                <span className="text-base font-medium tracking-wide">Publish Your Club</span>
                <div className="rounded-full bg-[#082F8A]">
                  <ArrowUpRightIcon className="text-white" size={40} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-20 lg:px-12">
        <div className="relative z-20 mt-14 flex flex-col gap-3 rounded-3xl border border-[#dfe6f5] bg-white p-4 shadow-[0_18px_45px_rgba(8,47,138,0.12)] sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-2xl border border-[#e3e8f5] bg-[#f5f7fc] px-3">
            <Search size={18} className="text-[#5770ab]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by club name or city..."
              className="w-full bg-transparent py-2.5 text-sm font-medium outline-none"
            />
          </div>
          <select value={sport} onChange={(e) => setSport(e.target.value)}
                  className="rounded-2xl border border-[#e3e8f5] bg-[#f5f7fc] px-3 py-2.5 text-sm font-medium outline-none sm:w-44">
            {sportOptions().map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div className="mb-5 mt-7 flex items-end justify-between">
          <h3 className="font-bebas text-2xl font-bold uppercase tracking-wide text-[#06195f] sm:text-3xl">Sports Clubs</h3>
          {!listQuery.isLoading ? (
            <span className="text-sm font-semibold text-[#5770ab]">Showing {clubs.length} {clubs.length === 1 ? 'club' : 'clubs'}</span>
          ) : null}
        </div>

        {listQuery.isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : clubs.length === 0 ? (
          <div className="py-16 text-center text-[#5770ab]">
            <Building2 className="mx-auto mb-3 text-[#9aa8c9]" size={44} />
            <p className="font-bold">No clubs found</p>
            <p className="text-sm">Try a different sport or search — or be the first to list your club.</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {clubs.map((c) => <ClubCard key={c.uid} c={c} onOpen={() => setActive(c)} />)}
          </div>
        )}
      </div>

      <ClubDetailModal club={active} onClose={() => setActive(null)} />

      <FooterSection />
    </main>
  )
}

function ClubCard({c, onOpen}: {c: any; onOpen: () => void}) {
  const poster = c.image_url || c.logo_url
  const location = [c.city, c.country].filter(Boolean).join(', ')
  const sports = (c.sport_types || '').split(',').map((s: string) => s.trim()).filter(Boolean)

  return (
    <button onClick={onOpen}
            className="group flex flex-col overflow-hidden rounded-[26px] border border-[#dfe6f5] bg-white text-left shadow-[0_10px_30px_rgba(8,47,138,0.08)] transition hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(8,47,138,0.16)]">
      <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#082f8a] to-[#18a85d]">
        {poster
          ? <img src={poster} alt={c.name} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
          : <div className="grid h-full place-items-center text-white/80"><Building2 size={48} /></div>}
        <div className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-black/45 to-transparent pb-4 pt-10">
          <span className="rounded-full bg-white px-5 py-2 text-sm font-bold text-[#082f8a] shadow-lg transition group-hover:bg-[#082f8a] group-hover:text-white">View Details</span>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="min-w-0">
          <h3 className="truncate text-base font-black text-[#06195f]">{c.name}</h3>
          {sports.length ? <p className="truncate text-xs font-medium capitalize text-[#8094bf]">{sports.map(sportLabel).join(' · ')}</p> : null}
        </div>
        {location ? (
          <div className="flex items-center gap-2.5">
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#082f8a] text-white"><MapPin size={14} /></span>
            <span className="truncate text-sm font-semibold text-[#27325c]">{location}</span>
          </div>
        ) : null}
      </div>
    </button>
  )
}

function ClubDetailModal({club, onClose}: {club: any | null; onClose: () => void}) {
  return (
    <Modal isOpen={!!club} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        {club ? (
          <>
            <ModalHeader className="flex flex-col gap-1 font-bebas text-2xl text-[#06195f]">{club.name}</ModalHeader>
            <ModalBody className="pb-6">
              {(club.image_url || club.logo_url) ? (
                <img src={club.image_url || club.logo_url} alt={club.name}
                     className="h-48 w-full rounded-2xl object-cover" />
              ) : null}

              {club.sport_types ? (
                <div className="flex flex-wrap gap-2">
                  {(club.sport_types || '').split(',').map((s: string) => s.trim()).filter(Boolean).map((s: string) => (
                    <span key={s} className="rounded-full bg-[#eef4ff] px-3 py-1 text-xs font-bold text-[#2168ff]">{sportLabel(s)}</span>
                  ))}
                </div>
              ) : null}

              {club.description ? <p className="whitespace-pre-line text-sm leading-relaxed text-[#27325c]">{club.description}</p> : null}

              <div className="grid gap-2 text-sm">
                {[club.address, club.city, club.country].filter(Boolean).length ? (
                  <DetailRow icon={<MapPin size={16} />}>{[club.address, club.city, club.country].filter(Boolean).join(', ')}</DetailRow>
                ) : null}
                {club.phone ? <DetailRow icon={<Phone size={16} />}><a href={`tel:${club.phone}`} className="text-[#2168ff]">{club.phone}</a></DetailRow> : null}
                {club.email ? <DetailRow icon={<Mail size={16} />}><a href={`mailto:${club.email}`} className="text-[#2168ff]">{club.email}</a></DetailRow> : null}
                {club.website ? <DetailRow icon={<Globe size={16} />}><a href={club.website} target="_blank" rel="noopener noreferrer" className="truncate text-[#2168ff]">{club.website}</a></DetailRow> : null}
              </div>

              <ClubMap lat={club.lat} lng={club.lng} name={club.name} />

              {(club.lat && club.lng) ? (
                <a href={`https://www.google.com/maps/search/?api=1&query=${club.lat},${club.lng}`}
                   target="_blank" rel="noopener noreferrer"
                   className="inline-flex w-fit items-center gap-2 rounded-full bg-[#082f8a] px-5 py-2 text-sm font-bold text-white transition hover:bg-[#0a37a0]">
                  <Navigation size={15} /> Get directions
                </a>
              ) : null}
            </ModalBody>
          </>
        ) : null}
      </ModalContent>
    </Modal>
  )
}

function ClubMap({lat, lng, name}: {lat?: number | null; lng?: number | null; name?: string}) {
  const {isLoaded} = useJsApiLoader({
    id:               'google-map-script',
    googleMapsApiKey: config.googleMapsApiKey,
  })

  if (!lat || !lng) return null
  const center = {lat: Number(lat), lng: Number(lng)}

  return (
    <div className="h-56 overflow-hidden rounded-2xl">
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{width: '100%', height: '100%'}}
          center={center}
          zoom={15}
          options={{disableDefaultUI: true, zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: false}}
        >
          <MarkerF position={center} title={name} />
        </GoogleMap>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[#f5f7fc] text-sm text-[#9aa8c9]">Loading map…</div>
      )}
    </div>
  )
}

function DetailRow({icon, children}: {icon: React.ReactNode; children: React.ReactNode}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#082f8a] text-white">{icon}</span>
      <span className="truncate font-semibold text-[#27325c]">{children}</span>
    </div>
  )
}
